import { Button, ConfirmDialog, FormError, Input, Label } from "@simplicity/ui";
import { useId, useState } from "react";
import { formatRelativeTime } from "../../lib/formatRelativeTime";
import { formatDateTime } from "./onsiteModel";
import { sftpSecretName, sftpSecretUrl, sftpUsernameError, sftpUsersOf } from "./sftpUsersModel";
import type { OnsiteWorkspace, SftpUser, SftpUsersChange } from "./types";
import { useChangeSftpUsers, useRefreshSftpUsers } from "./useOnsite";

type UserAction = { kind: "rotate" | "remove"; username: string };

function changeFor({ kind, username }: UserAction): SftpUsersChange {
  return kind === "rotate" ? { rotate: [username] } : { remove: [username] };
}

/** Users whose new password the operator will need to hand over. */
function handOverNames(change: SftpUsersChange | undefined): string[] {
  return [...(change?.add ?? []), ...(change?.rotate ?? [])];
}

function LastSet({ at }: { at: string | null }) {
  if (!at) return <span className="col-muted">unknown</span>;
  return (
    <span>
      {formatRelativeTime(Date.parse(at))}{" "}
      <span className="text-xs text-muted-foreground">({formatDateTime(at)})</span>
    </span>
  );
}

function UsersTable({
  users,
  disabled,
  removeHintId,
  onAction,
}: {
  users: SftpUser[];
  disabled: boolean;
  removeHintId: string;
  onAction: (action: UserAction) => void;
}) {
  if (users.length === 0) {
    return <p className="text-sm text-muted-foreground">No SFTP users yet.</p>;
  }
  return (
    <div className="admin-table-container">
      <table className="admin-table" aria-label="SFTP users">
        <thead>
          <tr>
            <th>Username</th>
            <th>Password last set</th>
            <th>
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {users.map(({ username, lastSetAt }) => (
            <tr key={username}>
              <td className="font-mono text-xs">{username}</td>
              <td>
                <LastSet at={lastSetAt} />
              </td>
              <td>
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={disabled}
                    aria-label={`Rotate password for ${username}`}
                    onClick={() => onAction({ kind: "rotate", username })}
                  >
                    Rotate password
                  </Button>
                  <Button
                    size="sm"
                    variant="destructiveOutline"
                    disabled={disabled}
                    aria-label={`Remove ${username}`}
                    aria-describedby={removeHintId}
                    onClick={() => onAction({ kind: "remove", username })}
                  >
                    Remove
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AddUserForm({
  existing,
  disabled,
  onAdd,
}: {
  existing: string[];
  disabled: boolean;
  onAdd: (username: string, done: () => void) => void;
}) {
  const id = useId();
  const [name, setName] = useState("");
  const error = name ? sftpUsernameError(name, existing) : null;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name || error) return;
    onAdd(name, () => setName(""));
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-1">
      <Label htmlFor={id} className="form-label">
        New username
      </Label>
      <div className="flex flex-wrap items-center gap-3">
        <Input
          id={id}
          value={name}
          onChange={(e) => setName(e.target.value.trim())}
          placeholder="e.g. vendor1"
          aria-invalid={error ? true : undefined}
          className="max-w-48"
          autoComplete="off"
        />
        <Button type="submit" disabled={disabled || !name || error !== null}>
          Add user
        </Button>
      </div>
      {error && <FormError>{error}</FormError>}
    </form>
  );
}

function HandOver({ workspace, usernames }: { workspace: OnsiteWorkspace; usernames: string[] }) {
  const secret = sftpSecretName(workspace);
  return (
    <output className="block space-y-1 rounded border border-border p-3 text-sm">
      <span className="block text-foreground">
        Requested for {usernames.join(", ")}. When the run succeeds, the new password is in the
        client&apos;s Secret Manager — secret <code>{secret}</code>, latest version. Open it while
        signed in as the client&apos;s Simplicity Google account.
      </span>
      <a
        href={sftpSecretUrl(workspace)}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-block text-primary underline-offset-4 hover:underline"
      >
        Open {secret} in Secret Manager
      </a>
    </output>
  );
}

const CONFIRM_COPY = {
  rotate: (username: string) => ({
    title: `Rotate ${username}'s password?`,
    description:
      "The agent generates a new password inside the client's project and the SFTP server applies it within a minute. The current password stops working.",
    confirmLabel: "Rotate password",
  }),
  remove: (username: string) => ({
    title: `Remove ${username}?`,
    description: `Removing does not revoke ${username}'s access on the SFTP server yet: the account keeps its last password. Rotate the password first and wait a minute for the server to apply it, then remove the user.`,
    confirmLabel: "Remove",
  }),
};

function ConfirmUserAction({
  action,
  open,
  onOpenChange,
  onConfirm,
}: {
  action: UserAction | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (action: UserAction) => void;
}) {
  const copy = action ? CONFIRM_COPY[action.kind](action.username) : null;
  return (
    <ConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      title={copy?.title ?? ""}
      description={copy?.description ?? ""}
      onConfirm={() => action && onConfirm(action)}
      options={{ confirmLabel: copy?.confirmLabel, destructive: action?.kind === "remove" }}
    />
  );
}

/** Where the list comes from, and the control that reads it again from the client's secret. */
function ListSource({
  known,
  disabled,
  pending,
  onRefresh,
}: {
  known: boolean;
  disabled: boolean;
  pending: boolean;
  onRefresh: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      {known ? (
        <p className="text-xs text-muted-foreground">
          This is the list the agent last reported. Users added outside this panel appear after a
          refresh.
        </p>
      ) : (
        <p className="text-sm text-muted-foreground">
          The user list hasn&apos;t been read from the SFTP server yet.
        </p>
      )}
      <Button size="sm" variant="outline" disabled={disabled} onClick={onRefresh}>
        {pending ? "Refreshing..." : "Refresh list"}
      </Button>
    </div>
  );
}

function MutationError({ error }: { error: Error | null }) {
  if (!error) return null;
  return (
    <FormError assertive className="whitespace-pre-line">
      {error.message}
    </FormError>
  );
}

function ManageSftpUsers({ workspace, blocked }: { workspace: OnsiteWorkspace; blocked: boolean }) {
  const removeHintId = useId();
  const change = useChangeSftpUsers(workspace.id);
  const refresh = useRefreshSftpUsers(workspace.id);
  const [action, setAction] = useState<UserAction | null>(null);
  const [confirming, setConfirming] = useState(false);
  const users = sftpUsersOf(workspace);
  const pending = change.isPending || refresh.isPending;
  const disabled = blocked || pending || workspace.status === "disabled";
  const handOver = handOverNames(change.isSuccess ? change.variables : undefined);

  const ask = (next: UserAction) => {
    setAction(next);
    setConfirming(true);
  };

  return (
    <section className="form-card space-y-4" aria-label="SFTP users">
      <h3 className="text-lg text-foreground">SFTP users</h3>
      {users && (
        <UsersTable users={users} disabled={disabled} removeHintId={removeHintId} onAction={ask} />
      )}
      <ListSource
        known={users !== null}
        disabled={disabled}
        pending={refresh.isPending}
        onRefresh={() => refresh.mutate()}
      />
      {users && users.length > 0 && (
        <p id={removeHintId} className="text-xs text-muted-foreground">
          Remove does not revoke access on the SFTP server yet. Rotate the user&apos;s password
          first, then remove them.
        </p>
      )}
      <AddUserForm
        existing={(users ?? []).map((u) => u.username)}
        disabled={disabled}
        onAdd={(username, done) => change.mutate({ add: [username] }, { onSuccess: done })}
      />
      {blocked && (
        <p className="text-xs text-muted-foreground">
          Another run is pending or running; change users once it finishes.
        </p>
      )}
      <MutationError error={change.error} />
      <MutationError error={refresh.error} />
      {handOver.length > 0 && <HandOver workspace={workspace} usernames={handOver} />}
      <ConfirmUserAction
        action={action}
        open={confirming}
        onOpenChange={setConfirming}
        onConfirm={(confirmed) => change.mutate(changeFor(confirmed))}
      />
    </section>
  );
}

/** Add, rotate and remove the workspace's SFTP logins, once an apply has created the server. */
export function SftpUsersPanel({
  workspace,
  blocked,
}: {
  workspace: OnsiteWorkspace;
  blocked: boolean;
}) {
  if (!workspace.enableSftpServer) return null;
  if (workspace.appliedAt === null) {
    return (
      <p className="text-sm text-muted-foreground">
        SFTP users can be managed once this workspace has been applied.
      </p>
    );
  }
  return <ManageSftpUsers workspace={workspace} blocked={blocked} />;
}
